// PostHog — product analytics for the dashboard.
//
// Architecture:
//   Browser → PostHog Cloud (or self-hosted) via posthog-js autocapture.
//
// Required env vars:
//   VITE_POSTHOG_KEY              project API key (phc_...).
//                                 Required for PostHog to initialize.
//                                 Leave unset to silently disable
//                                 (safe for local dev and preview deploys
//                                 that don't need analytics).
//   VITE_POSTHOG_HOST  (optional) defaults to https://us.i.posthog.com
//
// Call order:
//   1. initPostHog()      — in main.tsx, before React mounts
//   2. identifyUser()     — after Keycloak auth resolves, in AuthProvider
//   3. resetPostHog()     — on logout and after impersonation ends
//
// Feature flags are DISABLED by config — staged rollouts are off-policy.

import posthog from "posthog-js";
import { getCaptureSink } from "./capture-sink";
import { attributionPersonProps, clearTouches, ownHosts, readTouches, rememberSignupTouch, signupSourceHeader } from "./attribution";
import { isInternalEmail, redactSecrets, type AnalyticsSurface, type EventName, type PropsFor } from "@fastn/core";

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/**
 * Redacts an element's TEXT from session replay.
 *
 * Put it on anything that renders a secret the user is meant to read: a
 * one-time API key, a token, a decrypted value. maskAllInputs does not help
 * there -- the value is text, not an input.
 */
export const PH_MASK = "posthog-mask";

/** Query/fragment params that carry auth material and must not be captured. */
const SENSITIVE_URL_PARAMS = new Set([
  "code", "state", "session_state", "id_token", "access_token", "refresh_token", "token",
]);

/**
 * Route paths whose id segment is itself a bearer credential.
 *
 * These are share links: whoever holds the URL can act on the resource, with no
 * further authentication. Autocapture stamps the full URL onto $current_url for
 * EVERY event, so an unredacted path means a working share token sitting in a
 * third-party analytics store indefinitely, against a named person, and in
 * session replay.
 *
 * The route list is the one in App.tsx; keep them in step.
 */
const SENSITIVE_PATH_SEGMENTS = /\/(setup|config-review|test-case-review|connect)\/[^/?#]+/g;

/**
 * Remove auth material from a URL while keeping it useful for analytics.
 *
 * The path is what analytics needs; the fragment on the Keycloak callback is
 * what leaks. Rewritten rather than dropped so the pageview still says which
 * page was viewed — `/setup/:id` is exactly as useful for a funnel as the real
 * id, and carries none of the risk.
 */
export function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw, "http://localhost");
    let touched = false;
    for (const k of [...u.searchParams.keys()]) {
      if (SENSITIVE_URL_PARAMS.has(k.toLowerCase())) { u.searchParams.set(k, "redacted"); touched = true; }
    }
    const redactedPath = u.pathname.replace(SENSITIVE_PATH_SEGMENTS, "/$1/:id");
    if (redactedPath !== u.pathname) { u.pathname = redactedPath; touched = true; }
    // The fragment is not parsed as params by URL, and on the OAuth callback it
    // is where the code lives — so it is dropped wholesale rather than picked
    // through. Nothing in this app routes on the fragment.
    if (u.hash && /(^|[#&])(code|state|session_state|access_token|id_token)=/i.test(u.hash)) {
      u.hash = ""; touched = true;
    }
    if (!touched) return raw;
    return raw.startsWith("http") ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return raw;
  }
}

/**
 * Routes that must never appear in a session recording.
 *
 * Two different reasons, both non-negotiable:
 *
 *   Customer data — /activity, /events, /traces and execution detail render
 *   the partner's customers' business records, including a raw <pre> payload
 *   block. Recording those puts a customer's data in a third-party store under
 *   an agreement they never saw.
 *
 *   Credentials — /profile and /settings render API keys, secrets and security
 *   metadata. PH_MASK covers the elements it is applied to; suppressing the
 *   whole route is the belt to that braces, because a newly added field on
 *   those pages is unmasked until someone remembers to mark it.
 *
 * The embed is absent from this list because it is not recorded AT ALL at
 * launch — recording a partner's customers on the partner's own domain needs
 * contractual cover fastn should not assume it has.
 */
const REPLAY_BLOCKED_ROUTES = [
  "/activity",
  "/events",
  "/traces",
  "/profile",
  "/settings",
] as const;

/** Whether this path renders something that must not be recorded. */
export function isReplayBlockedRoute(pathname: string): boolean {
  // Compared against the path WITHOUT the /gcp base: the dashboard is served
  // from both the domain root and /gcp/ on the CloudFront edge, and a prefix
  // check against the raw pathname silently stops matching under /gcp — which
  // would leave exactly one of the two deployments recording customer data.
  const path = pathname.replace(/^\/gcp(?=\/|$)/, "") || "/";
  return REPLAY_BLOCKED_ROUTES.some((r) => path === r || path.startsWith(`${r}/`));
}

/**
 * Start or stop replay for the route just entered.
 *
 * Called on every route change from PostHogPageviews. Suppression has to be
 * driven by navigation rather than configured once, because this is a SPA: the
 * recorder keeps running across route changes unless something stops it.
 */
export function applyReplayPolicy(pathname: string): void {
  if (!_initialized) return;
  try {
    if (isReplayBlockedRoute(pathname)) posthog.stopSessionRecording();
    else posthog.startSessionRecording();
  } catch {
    // Replay control is best-effort; never break navigation for it.
  }
}

/** Whether a host is PostHog's own cloud, as opposed to a reverse proxy. */
export function isPostHogOrigin(host: string): boolean {
  try {
    return new URL(host).hostname.endsWith(".posthog.com");
  } catch {
    return false;
  }
}

let _initialized = false;
// Set once identify() has run, so nothing is ever captured anonymously.
let _identified = false;

/**
 * Events captured before identify() ran, sent by it with their own time.
 * Dropping them lost the first onboarding_screen_viewed of every session that
 * opened /onboarding directly (a new signup, a reload, a nurture email link):
 * the screen mounts a moment before identify() finishes, so someone who left
 * from that first screen was missing from every "where people stopped" chart
 * (dev, 23 Oct 2026). Bounded, because a session that is never identified
 * must not grow it forever.
 */
const EARLY_LIMIT = 50;
let _early: Array<{ event: string; properties: Record<string, unknown>; at: Date }> = [];

function holdUntilIdentified(event: string, properties: Record<string, unknown>): void {
  if (_early.length < EARLY_LIMIT) _early.push({ event, properties, at: new Date() });
}

/**
 * A nurture email's link lands with `?nudge=<id>&utm_campaign=<email_key>`.
 * Read once at startup, before the router or the Keycloak round trip can drop
 * the query, and sent as nurture_email_clicked once the person is identified.
 */
let _pendingNudge: { nudge_id: string; email_key: string } | null = null;

export function readNudgeParams(search: string): { nudge_id: string; email_key: string } | null {
  try {
    const q = new URLSearchParams(search);
    const id = q.get("nudge");
    if (!id || !/^[A-Za-z0-9_-]{6,64}$/.test(id)) return null;
    const key = q.get("utm_campaign");
    return { nudge_id: id, email_key: key && /^[a-z_]{3,40}$/.test(key) ? key : "unknown" };
  } catch {
    return null;
  }
}

/** Initialize PostHog. Safe to call multiple times — only runs once. */
/**
 * Runtime bootstrap, for a surface that cannot be handed a build-time key.
 *
 * The embed is the reason this exists: it is built inside the service image
 * (deploy/docker/Dockerfile.service), where no `VITE_*` build arg reaches it,
 * so it receives its key from the API at mount instead. It bundles THIS module
 * — 143 dashboard files end up in the widget, five of them instrumented — so
 * the embed must start this singleton rather than a second one beside it, or
 * every `capture()` inside those shared components stays a silent no-op.
 */
export interface PostHogBootstrap {
  key: string;
  host?: string;
  /**
   * SDK options merged over the defaults below. A surface with different rules
   * about what it may record — the embed runs on a partner's page, watching
   * their customers — overrides them here rather than forking init().
   */
  overrides?: Record<string, unknown>;
}

export function initPostHog(bootstrap?: PostHogBootstrap): void {
  if (_initialized) return;

  // Skip on dev/preview builds so localhost sessions don't flood prod
  // analytics with replay payloads. Anyone wanting PostHog locally can
  // run a prod build (`vite build && vite preview`).
  //
  // A runtime bootstrap is exempt: the caller was handed a key by the server,
  // which is a deliberate act by a deployed environment and a stronger signal
  // than this bundle's own build mode.
  if (typeof window !== "undefined") _pendingNudge = readNudgeParams(window.location.search);

  if (!bootstrap && !import.meta.env.PROD) {
    console.info("[posthog] disabled — non-production build (import.meta.env.PROD =", import.meta.env.PROD, ")");
    return;
  }

  const apiKey = bootstrap?.key ?? (import.meta.env.VITE_POSTHOG_KEY as string | undefined);
  const apiHost = bootstrap?.host
    ?? (import.meta.env.VITE_POSTHOG_HOST as string | undefined)
    ?? DEFAULT_POSTHOG_HOST;

  if (!apiKey) {
    // Say so. The non-prod skip above logs; this path did not, so a PRODUCTION
    // build shipped with analytics silently off and looked identical to one
    // that worked. Three of the four prod dashboard builds were missing the
    // key and nobody could tell from the browser — the only symptom was users
    // missing from PostHog, which reads as an analytics problem rather than a
    // build problem.
    console.warn("[posthog] disabled — VITE_POSTHOG_KEY is not set in this build");
    return;
  }

  posthog.init(apiKey, {
    // Strip credential material out of every captured URL.
    //
    // The Keycloak callback lands on `/#state=…&code=…&session_state=…`, and
    // that whole string was being stored as $current_url against a named
    // person, and replayed in session recordings. The code is single-use and
    // short-lived, but it is auth material sitting in a third-party analytics
    // store, so it should never have left the browser.
    sanitize_properties: (props) => {
      for (const key of ["$current_url", "$referrer", "$pathname"]) {
        const v = props[key];
        if (typeof v === "string") props[key] = scrubUrl(v);
      }
      // Credential scrub runs over EVERY property, not a named list.
      //
      // The named-list approach above is right for URLs, because we know which
      // properties hold one. It is wrong for secrets: the leak this fixes was
      // in `$el_text` and the `$elements` chain, properties the SDK fills in
      // and we never named. Anything credential-shaped is replaced wherever it
      // appears. KAT-6483.
      return redactSecrets(props);
    },
    api_host: apiHost,
    // REQUIRED whenever api_host is a proxy rather than PostHog itself.
    //
    // The SDK derives the toolbar and session-replay-player origins from
    // api_host unless told otherwise, so a proxied deployment loads both from
    // the proxy path — which serves ingest, not an app — and they silently do
    // not work. Setting it unconditionally would be wrong too: pointing
    // ui_host at us.posthog.com for a self-hosted instance breaks the same two
    // things in the other direction. So it is derived from whether api_host is
    // actually PostHog.
    ...(isPostHogOrigin(apiHost) ? {} : { ui_host: "https://us.posthog.com" }),
    // Autocapture covers clicks and form submits — the baseline "see what users
    // are doing" signal that custom events layer on top of.
    //
    // SCOPED, not switched off. Unscoped autocapture on a dashboard this size
    // is mostly div clicks: it is the single biggest driver of event volume and
    // most of that volume carries no signal. The allowlist keeps the events
    // that name an actual control.
    //
    // Pairs with a project-side setting: `data-testid` is added to the captured
    // attributes in Project settings -> Autocapture. There are ~2,875 of them
    // in this app already, enforced by convention, so every autocaptured click
    // becomes a stable readable selector instead of a brittle CSS path that
    // breaks on the next restyle.
    autocapture: {
      element_allowlist: ["button", "a", "input", "select", "textarea"],
    },
    // The dashboard is always authenticated and nothing is captured before
    // identify (see capture_pageview below), so anonymous person profiles are
    // pure cost. This is already the SDK default; stated so a future `defaults`
    // bump cannot change it silently.
    person_profiles: "identified_only",
    // ── Struggle and health signals ──────────────────────────────────────
    // All four are OFF by default in posthog-js and all four were off here.
    //
    // capture_exceptions turns on Error Tracking, which groups client
    // exceptions into issues; broken down by app_version it answers "did the
    // last release break anything, and for whom". Web vitals produce
    // $web_vitals for the p75-LCP-by-route chart. Heatmaps are viewed through
    // the toolbar rather than an insight.
    capture_exceptions: true,
    capture_performance: { web_vitals: true },
    capture_heatmaps: true,
    // Pageviews are captured MANUALLY, once the user is identified — see
    // capturePageview() and identifyUser() below.
    //
    // With this true, posthog-js captures a $pageview the moment init() runs.
    // init() runs in main.tsx BEFORE React mounts, while Keycloak's round trip
    // has not returned, so those events carry an anonymous UUIDv7 distinct id
    // and PostHog shows them as `01a06173-…` instead of an email. That is what
    // made the Activity feed unusable for "who was in the demo": the rows on
    // `/` and `/profile` — the ones right after login — were exactly the ones
    // captured before identify.
    capture_pageview: false,
    capture_pageleave: true,
    // Session replay masking.
    //
    // maskAllInputs covers <input>, <textarea> and <select> VALUES. It does
    // nothing for text the app renders itself, and a credential shown for
    // copying is rendered text -- so the one-time API key was going into
    // replay in full. maskTextSelector is what covers that, and it only works
    // on elements that actually carry the class: it was pointing at
    // `.posthog-mask`, which nothing in the app used, so no text was masked
    // anywhere.
    //
    // Anything that renders a credential, a token, or a user's own security
    // metadata must carry PH_MASK. It is exported rather than written inline so
    // the call sites are greppable from here.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: `.${PH_MASK}`,
      // Recorded network requests carry the same URLs as captured events, so
      // they need the same redaction — otherwise the OAuth callback and every
      // share-link path that scrubUrl strips from $current_url reappear in the
      // replay's network panel. One function, both uses.
      //
      // Request and response BODIES stay off (the default) and must not be
      // enabled: this product moves the partner's customers' business data.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: typeof request.name === "string" ? scrubUrl(request.name) : request.name,
      }),
    },
    // Feature flags are off by policy — we do not run staged rollouts.
    advanced_disable_feature_flags: true,
    // Quieter console in dev; flip on if you need to debug the SDK.
    loaded: () => {
      _initialized = true;
    },
    // Last, so a surface with stricter rules than the dashboard's can actually
    // override them. The embed tightens masking and turns OFF console and
    // network capture that are ON here.
    ...(bootstrap?.overrides ?? {}),
  });

  _initialized = true;
  clearPersistedOnboardingContext();
}

/** The org/workspace/tenant context an event is attributed to. */
export interface AnalyticsContext {
  email?: string;
  role?: string;
  /**
   * Which product this session is. Defaults to "dashboard" because that is what
   * this bundle almost always is — but the SAME bundle also serves a scoped
   * setup link (auth.tsx LINK_TOKEN), which is a different product with a
   * different audience: a partner's end customer, arriving from an emailed link,
   * doing one focused task. Hardcoding "dashboard" folded those sessions into
   * the operator numbers and made setup-link completion unmeasurable.
   */
  surface?: AnalyticsSurface;
  /** The org being acted in. For an embed/tenant session this is the tenant. */
  orgId?: string;
  accountName?: string;
  /** `parentOrgId ?? orgId` — the partner, and the unit that renews. */
  workspaceId?: string;
  workspaceName?: string;
  /** Set only when acting inside a tenant workspace nested under a partner. */
  tenantId?: string | null;
  plan?: string | null;
  isImpersonating?: boolean;
}

/**
 * Register the property envelope and the three groups.
 *
 * Split from identifyUser() because the two resolve at DIFFERENT TIMES. Identity
 * is known the moment Keycloak returns; the workspace, plan and tenant come from
 * the memberships response, which lands later — and all three change again on an
 * account switch. Folding this into identifyUser() would mean either capturing
 * the landing pageview twice or attributing every early event to a null
 * workspace, so this is safe to call repeatedly and identifyUser() is not.
 */
export function updateAnalyticsContext(ctx: AnalyticsContext): void {
  if (!_initialized) return;

  _currentOrg = ctx.orgId ?? null;
  // A microtask, so identifyUser() has called posthog.identify() before
  // anything held for the org is sent under this person's id.
  if (ctx.orgId && _identified && !_orgKnown) queueMicrotask(releaseAwaitingOrg);

  // ── Super properties: attached to every event from here on ───────────────
  //
  // The cheapest thing in the whole measurement plan and the least recoverable.
  // `is_internal` in particular cannot be backfilled: without it from the first
  // event, staff dogfooding is permanently indistinguishable from customer
  // behaviour, and most of the team works in this app daily.
  const workspaceId = ctx.workspaceId ?? ctx.orgId ?? null;
  posthog.register({
    surface: ctx.surface ?? "dashboard",
    source_system: "platform",
    app_version: (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? null,
    role: ctx.role ?? null,
    org_id: ctx.orgId ?? null,
    // The names ride on every event, not only on the groups below. A
    // destination reading the group lookup alone answered "org unknown" for an
    // org entered seconds earlier: its $groupidentify had not reached the
    // destination's group cache yet, though the event carried the org id.
    org_name: ctx.accountName ?? null,
    workspace_id: workspaceId,
    workspace_name: ctx.workspaceName ?? (workspaceId === ctx.orgId ? ctx.accountName ?? null : null),
    tenant_id: ctx.tenantId ?? null,
    plan: ctx.plan ?? null,
    is_internal: isInternalEmail(ctx.email),
    // Stops a support session reading as the customer's own behaviour.
    is_impersonating: ctx.isImpersonating ?? false,
  });

  // ── Groups: the units insights are actually asked about ──────────────────
  //
  // Without these every insight counts PEOPLE, and people are the least
  // meaningful unit in this product: a partner renews on whether their
  // customers' integrations work, not on how many of their engineers logged in.
  // Group membership is not retroactive — events sent before a group is
  // associated can never be rolled up to it, which is why this runs as early as
  // the data allows rather than at the first interesting event.
  //
  // GATED ON IDENTIFY, unlike register() above. register() only mutates local
  // state, but posthog.group() SENDS a $groupidentify event — so calling it
  // before identify attaches the org to an anonymous UUID, which is the same
  // bug the manual pageview path exists to prevent. Caught by driving a real
  // browser against a capture server: the only events the page produced were
  // two anonymous $groupidentify.
  if (!_identified) return;

  // Each group() SENDS a $groupidentify, and this function re-runs whenever the
  // auth context object changes identity -- which it does on every navigation.
  // Unguarded that was 2+ $groupidentify per route change: pure volume, billed,
  // carrying nothing new. Only re-send when the association or its properties
  // actually changed.
  if (ctx.orgId) {
    groupOnce("organization", ctx.orgId, {
      name: ctx.accountName,
      plan: ctx.plan ?? undefined,
    });
  }
  if (workspaceId) {
    groupOnce("workspace", workspaceId, {
      name: ctx.workspaceName ?? ctx.accountName,
    });
  }
  if (ctx.tenantId) {
    groupOnce("tenant", ctx.tenantId, { org_id: workspaceId });
  }
}

/* ── Held until the org is known ─────────────────────────────────────────────
 *
 * Since the account switcher, a token carries no org: the org arrives with the
 * memberships, after identify. The login event and the landing pageview were
 * sent in that gap, so every one of them arrived with no org_id and could not be
 * counted per account (measured: 100% of user_logged_in, a quarter of
 * pageviews). They are held until the org is set, and sent anyway after
 * ORG_WAIT_MS or when the page unloads, so a person with no org (a platform
 * admin) still counts. */
const ORG_WAIT_MS = 15_000;
let _currentOrg: string | null = null;
let _orgKnown = false;
let _awaitingOrg: Array<(orgId: string | null) => void> = [];
let _awaitTimer: ReturnType<typeof setTimeout> | null = null;
let _unloadHooked = false;

function releaseAwaitingOrg(): void {
  // Also after the wait runs out: a person with no org stops waiting, rather
  // than every later pageview sitting out the same 15 seconds.
  _orgKnown = true;
  if (_awaitTimer) clearTimeout(_awaitTimer);
  _awaitTimer = null;
  const pending = _awaitingOrg;
  _awaitingOrg = [];
  for (const send of pending) {
    try {
      send(_currentOrg);
    } catch {
      // One held event must not stop the rest.
    }
  }
}

function whenOrgKnown(send: (orgId: string | null) => void): void {
  if (_orgKnown) {
    send(_currentOrg);
    return;
  }
  _awaitingOrg.push(send);
  if (!_awaitTimer) _awaitTimer = setTimeout(releaseAwaitingOrg, ORG_WAIT_MS);
  if (!_unloadHooked && typeof window !== "undefined") {
    _unloadHooked = true;
    window.addEventListener("pagehide", releaseAwaitingOrg);
  }
}

/** Last association sent per group type, so an unchanged one is not re-sent. */
const _groups = new Map<string, string>();

function groupOnce(type: string, key: string, props: Record<string, unknown>): void {
  const fingerprint = `${key}:${JSON.stringify(props)}`;
  if (_groups.get(type) === fingerprint) return;
  _groups.set(type, fingerprint);
  posthog.group(type, key, props);
}

/**
 * Identify the current user and capture the landing pageview.
 *
 * Call once per session, after Keycloak auth resolves. Context that arrives
 * later — workspace, plan, tenant, impersonation — goes through
 * updateAnalyticsContext() instead, which is safe to re-run.
 */
export function identifyUser(user: { id: string; name?: string } & AnalyticsContext): void {
  if (!_initialized) return;
  _identified = true;

  updateAnalyticsContext(user);

  // Where they came from, recorded before the Keycloak round trip. Sent in the
  // $identify itself so it is on the person before anything else is: see
  // attribution.ts for why PostHog's own $initial_* could not be left alone.
  //
  // Only for a person signing in to the dashboard. A setup-link visitor
  // arrived from an emailed link, not a campaign.
  const touches = (user.surface ?? "dashboard") === "dashboard" ? readTouches() : null;
  const source = touches
    ? attributionPersonProps({ ...touches, ownHosts: appHosts(), scrubPath: scrubUrl })
    : null;
  rememberSignupTouch(touches?.first ?? null);
  posthog.identify(user.id, {
    ...source?.set,
    email: user.email,
    name: user.name,
    role: user.role,
  }, source?.setOnce);

  // The landing pageview, now that there is a name to attach it to, then
  // whatever the page captured while it waited, at the time it happened.
  capturePageview();
  const early = _early;
  _early = [];
  for (const e of early) posthog.capture(e.event, e.properties, { timestamp: e.at });

  if (_pendingNudge) {
    const nudge = _pendingNudge;
    _pendingNudge = null;
    capture("nurture_email_clicked", nudge);
  }
}

/**
 * Capture a $pageview. Called once from identifyUser() and then on every route
 * change (PostHogPageviews in App.tsx).
 *
 * A no-op until PostHog is identified: an event captured earlier would carry an
 * anonymous distinct id, which is the whole problem this replaces.
 */
let _lastPageview: string | null = null;

export function capturePageview(): void {
  if (!_initialized || !_identified) return;

  // Drop a repeat of the URL we just sent. The route effect that drives this is
  // keyed on the auth context as well as the location, and that object changes
  // identity on every navigation -- so each route change fired twice and every
  // pageview-derived number (sessions per page, funnel entry counts, the whole
  // web-analytics tab) read exactly 2x high. Deduping here rather than only in
  // the effect keeps the guarantee no matter which call site fires.
  const url = window.location.pathname + window.location.search;
  if (url === _lastPageview) return;
  _lastPageview = url;

  if (_orgKnown) {
    posthog.capture("$pageview");
    return;
  }
  // Held for the org; the URL is pinned now, because by the time it is sent the
  // person may be on another page.
  const href = window.location.href;
  const pathname = window.location.pathname;
  whenOrgKnown(() => posthog.capture("$pageview", { $current_url: href, $pathname: pathname }));
}

const appHosts = () => ownHosts(import.meta.env.VITE_KEYCLOAK_URL as string | undefined);

/**
 * `x-fastn-signup-source` for the request that may create the account; see
 * attribution.ts. Not behind the PostHog guard: the user record gets its source
 * whether or not PostHog is on.
 */
export function signupSourceHeaderValue(): string | null {
  return signupSourceHeader(scrubUrl, appHosts());
}

/** Clear user identity — call on logout and after impersonation ends. */
export function resetPostHog(): void {
  // Before the guard: the signup source is kept whether or not PostHog is on.
  clearTouches();
  if (!_initialized) return;
  _identified = false;
  _early = [];
  _lastPageview = null;
  _groups.clear();
  _currentOrg = null;
  _orgKnown = false;
  _awaitingOrg = [];
  if (_awaitTimer) clearTimeout(_awaitTimer);
  _awaitTimer = null;
  posthog.reset();
}

/**
 * Capture a product event.
 *
 * Typed against the catalogue in packages/core/src/analytics.ts, deliberately:
 * an untyped capture(name, props) makes a typo'd event name a silent SECOND
 * event that halves whatever funnel depended on the first, and nobody notices
 * for a quarter. Here it is a compile error.
 *
 * The same union is used by the services, so a client-reported intent and a
 * server-reported outcome land on the same name with the same property shapes
 * and can be joined in one funnel.
 *
 * A no-op until identify() has run: an event captured earlier carries an
 * anonymous distinct id, which is the bug the manual pageview path exists to
 * avoid — see capturePageview().
 */
export function capture<N extends EventName>(event: N, properties: PropsFor<N>): void {
  const sink = getCaptureSink();
  if (sink) {
    try {
      sink(event, properties as Record<string, unknown>);
    } catch {
      // Telemetry must never break the caller.
    }
    return;
  }
  if (!_initialized) return;
  if (!_identified) {
    holdUntilIdentified(event, withOnboarding(properties as Record<string, unknown>));
    return;
  }
  posthog.capture(event, withOnboarding(properties as Record<string, unknown>));
}


/**
 * capture(), held until the org is known, with an empty `org_id` property
 * filled in from it. For the events sent at sign-in, before the memberships
 * have said which org the person is in.
 */
export function captureWithOrg<N extends EventName>(event: N, properties: PropsFor<N>): void {
  if (!_initialized || !_identified) return;
  whenOrgKnown((orgId) => {
    const props = { ...(properties as Record<string, unknown>) };
    if ("org_id" in props && !props.org_id) props.org_id = orgId;
    posthog.capture(event, props);
  });
}

/**
 * Report a HANDLED error to Error Tracking.
 *
 * Exception autocapture only sees what reaches `window.onerror` — i.e. the
 * failures nobody caught. This product catches nearly all of its failures on
 * purpose and renders an error card, so the class of failure a customer
 * actually experiences was the one class never recorded: every captured
 * `$exception` had `handled: false`. KAT-6469.
 *
 * NOT typed against the event catalogue, unlike capture() above: an exception
 * is grouped by its own fingerprint and stack, not by a name we choose, so
 * there is no catalogue entry for it to typo.
 *
 * Guarded on _initialized only, not _identified: a request can fail before
 * identify resolves (an expired session on first load is exactly that), and
 * those are the failures most worth seeing.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (!_initialized) return;
  try {
    posthog.captureException(error, context);
  } catch {
    // Telemetry must never be the reason a request handler throws.
  }
}

/**
 * Capture an event from a `pagehide` / `beforeunload` handler.
 *
 * The ordinary capture() queues a normal request, and a page that is unloading
 * is free to be torn down before it leaves — so an event fired on the way out
 * is usually lost. `onboarding_abandoned` recorded ZERO in the 30 days to 15
 * September while 17 people started the wizard and neither skipped nor
 * completed it: every one of those abandonments was captured and then dropped
 * on unload.
 *
 * sendBeacon is the browser's answer to exactly this: the request is handed to
 * the user agent, which delivers it after the document is gone. PostHog's own
 * $pageleave already uses it, which is why that event survives and this one did
 * not.
 *
 * Only for the unload path. Beacon requests cannot be read back and are capped
 * in size, so everything else should stay on the default transport.
 */
export function captureOnUnload<N extends EventName>(event: N, properties: PropsFor<N>): void {
  if (!_initialized || !_identified) return;
  posthog.capture(event, withOnboarding(properties as Record<string, unknown>), { transport: "sendBeacon" });
}

const ONBOARDING_PROPS = ["onboarding_screen", "onboarding_step"] as const;

/**
 * The onboarding screen on show, or null. Kept in this page's memory and added
 * to every event capture() sends, so any event (an error, a failed request) can
 * be placed on the step the person was on without each call site knowing about
 * onboarding. Not a PostHog super property: those persist in the browser, and
 * a tab closed on an onboarding screen left every later event, days later and
 * nowhere near onboarding, stamped with that screen.
 */
let _onboardingCtx: { onboarding_screen: string; onboarding_step: number } | null = null;

function withOnboarding(properties: Record<string, unknown>): Record<string, unknown> {
  return _onboardingCtx ? { ..._onboardingCtx, ...properties } : properties;
}

export function registerOnboardingContext(ctx: { onboarding_screen: string; onboarding_step: number } | null): void {
  _onboardingCtx = ctx;
}

/** Drop the super properties an earlier build persisted for the onboarding screen. */
function clearPersistedOnboardingContext(): void {
  try {
    for (const k of ONBOARDING_PROPS) posthog.unregister(k);
  } catch {
    // Telemetry must never break start-up.
  }
}
